import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { ADMIN_ACCESS_CODES, AppCtx, firstAccessibleView, SECTION_CODE, useApp, View, viewAllowed } from '../App';
import { Bell } from './Bell';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Profile } from '../profile/Profile';
import { Avatar } from '../components/Avatar';
import { IconCollapse, IconExit, IconGear } from '../components/icons';
import { mobileTabs, TabBar } from './TabBar';
import { Logo } from '../components/Logo';
import { ChatProvider, useChat } from '../chat/ChatProvider';
import { useIsMobile } from '../lib/media';
import { mobileModules } from '../nav/mobile';
import { SECTIONS } from '../nav/sections';
import { CallProvider } from '../chat/call/CallProvider';
import { CallLayer } from '../chat/call/CallLayer';
import { CallSummary } from '../chat/call/CallSummary';
import { UpdateBar } from './UpdateBar';
import { positionsLabel } from '../lib/positionsLabel';
import { forgetPinLogin } from '../lib/pin';
import { tellTabs } from '../lib/tabs';

/*
 * Разделы грузятся отдельными файлами, когда в них заходят. Стопка проектов
 * тянет за собой leaflet и математику графика, чат и календарь — свои
 * экраны: держать это в общем файле значит заставлять каждого ждать то,
 * чем он, возможно, сегодня не воспользуется.
 */
const KbStack = lazy(() => import('../knowledge/SectionStacks').then((m) => ({ default: m.KbStack })));
const FunctionsSection = lazy(() => import('../functions/FunctionsSection').then((m) => ({ default: m.FunctionsSection })));
const Admin = lazy(() => import('../admin/Admin').then((m) => ({ default: m.Admin })));
const ContractsSection = lazy(() =>
  import('../contracts/ContractsSection').then((m) => ({ default: m.ContractsSection })),
);
const LegalSection = lazy(() => import('../legal/LegalSection').then((m) => ({ default: m.LegalSection })));
const FinanceSection = lazy(() => import('../payments/FinanceSection').then((m) => ({ default: m.FinanceSection })));
const MoreScreen = lazy(() => import('./MoreScreen').then((m) => ({ default: m.MoreScreen })));
const MyAssignments = lazy(() =>
  import('../assignments/MyAssignments').then((m) => ({ default: m.MyAssignmentsScreen })),
);
const ChatScreen = lazy(() => import('../chat/ChatScreen').then((m) => ({ default: m.ChatScreen })));
const CalendarScreen = lazy(() => import('../calendar/CalendarScreen').then((m) => ({ default: m.CalendarScreen })));
const HomeScreen = lazy(() => import('./HomeScreen').then((m) => ({ default: m.HomeScreen })));

/**
 * Доступен ли раздел: назначение есть, а на телефоне ещё и раскладка не
 * выключила его. «Администрирование» и «Ещё» — особые случаи: у первого нет
 * своей записи, он открыт по любой из вкладок; второе — сама полоса, не
 * запись.
 */
function sectionOk(app: AppCtx, isMobile: boolean, navKey: View): boolean {
  if (navKey === 'admin') return ADMIN_ACCESS_CODES.some((c) => app.hasAccess(c));
  if (navKey === 'more') return true;
  if (!viewAllowed(app.hasAccess, navKey, isMobile)) return false;
  if (SECTION_CODE[navKey] && isMobile && !mobileModules(app.mobile).includes(navKey)) return false;
  return true;
}

export function Shell() {
  const app = useApp();
  // поток событий чата подключается один раз на всё приложение: от него
  // зависят и счётчик непрочитанного в меню, и уведомления на рабочем столе
  return (
    <ChatProvider>
      <AccountSync />
      {/* звонок должен доходить в любом разделе, а не только в чате */}
      <CallProvider>
        <ShellInner />
        <CallLayer />
        {/* итоги звонка из уведомления — тоже поверх любого раздела */}
        {app.summaryCallId && <CallSummary callId={app.summaryCallId} onClose={app.closeCallSummary} />}
      </CallProvider>
    </ChatProvider>
  );
}

/**
 * Администратор щёлкает по экрану доступов, и событие `access` приходит
 * пачкой. Профиль перечитывается один раз, когда пачка утихла.
 */
const ACCESS_SETTLE_MS = 1000;

/*
 * Сервер говорит о человеке целиком (спека 2026-10-02-tabs-devices-sync):
 * сессию закрыли (отключён, сменён пароль) — на окно входа с причиной;
 * доступы поменялись — перечитать профиль, меню перестроится само.
 */
function AccountSync() {
  const app = useApp();
  const chat = useChat();
  // контекст приложения меняется часто, а отложенное перечитывание должно
  // пережить переподписку и взять свежий
  const appRef = useRef(app);
  appRef.current = app;
  const accessTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (accessTimer.current) clearTimeout(accessTimer.current);
    },
    [],
  );
  useEffect(
    () =>
      chat.subscribe((e) => {
        if (e.type === 'session') {
          // пин-код при смене пароля сброшен: запомненный вход по нему не сработает
          if (e.data.reason === 'password') forgetPinLogin();
          tellTabs({ type: 'logout', reason: e.data.reason });
          app.endSession(e.data.reason);
        } else if (e.type === 'access') {
          if (accessTimer.current) clearTimeout(accessTimer.current);
          accessTimer.current = setTimeout(() => {
            accessTimer.current = null;
            void appRef.current.refreshAccess();
          }, ACCESS_SETTLE_MS);
        }
      }),
    [app, chat],
  );
  return null;
}

function ShellInner() {
  const app = useApp();
  const chat = useChat();
  const [min, setMin] = useState(() => localStorage.getItem('code-sb-min') === '1');
  // Пока человек печатает, нижняя полоска не нужна — клавиатура и так съела
  // полэкрана. Фокус в поле ввода ставит класс typing, уход — снимает.
  const [typing, setTyping] = useState(false);
  const isTextField = (t: EventTarget | null) => {
    const el = t as HTMLElement | null;
    if (!el) return false;
    if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
    if (el.tagName !== 'INPUT') return false;
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'file', 'button', 'submit', 'range'].includes(type);
  };

  const isMobile = useIsMobile();
  // Доступен ли текущий раздел: назначение есть, и на телефоне раскладка его
  // не выключила. Решение берётся по ключу навигации, поэтому подстраницы
  // раздела (проект, оргструктура) наследуют его.
  const ok = sectionOk(app, isMobile, app.view);
  const adminAccessible = sectionOk(app, isMobile, 'admin');
  const name = `${app.user.firstName} ${app.user.lastName}`.trim() || app.user.login;

  // Прямой заход в недоступный раздел уводит молча на первый доступный —
  // без заглушки, человек не должен узнавать о существовании раздела из
  // сообщения о запрете (решение Ильи 12.09.2026).
  useEffect(() => {
    // Недоступный раздел — на Главную (телефон: первая вкладка); на телефоне
    // Главной в полосе нет, корень открывает первую вкладку. go уведомляет
    // всегда, поэтому без сравнения с текущим разделом эффект зациклится.
    const to = !ok
      ? firstAccessibleView(app, isMobile)
      : isMobile && app.view === 'home' ? (mobileTabs(app).tabs[0] as View) : null;
    if (to && to !== app.view) app.showView(to);
  }, [ok, isMobile, app]);

  const go = (v: View) => app.showView(v);

  return (
    <div className={`app${min ? ' sbmin' : ''}${typing ? ' typing' : ''}`} id="app">
      <aside className="sb" aria-label="Меню">
        <div className="sb-top">
          <a className="sb-brand" tabIndex={0} title="На главную" onClick={() => go('home')}>
            {/* полный знак — в развёрнутом меню, буква «C» — в свёрнутом (переключение в CSS) */}
            <Logo variant="full" className="sb-logo-full" />
            <Logo variant="mark" className="sb-logo-mark" />
          </a>
          <Bell variant="desktop" />
        </div>
        <nav className="sb-nav">
          {SECTIONS.filter((n) => viewAllowed(app.hasAccess, n.v)).map((n) => (
            <a
              key={n.v}
              className={`sb-item${app.view === n.v ? ' on' : ''}`}
              tabIndex={0}
              role="button"
              title={n.title}
              onClick={() => go(n.v)}
            >
              <n.Icon />
              <span>{n.title}</span>
              {n.v === 'chat' && chat.unreadTotal > 0 && (
                <span className="sb-badge">{chat.unreadTotal > 99 ? '99+' : chat.unreadTotal}</span>
              )}
            </a>
          ))}
        </nav>
        {adminAccessible && (
          <nav className="sb-nav sb-nav-btm">
            <a
              className={`sb-item${app.view === 'admin' ? ' on' : ''}`}
              tabIndex={0}
              role="button"
              title="Администрирование"
              onClick={() => go('admin')}
            >
              <IconGear />
              <span>Администрирование</span>
            </a>
          </nav>
        )}
        <div className="sb-acct">
          <div
            className="acct-u"
            role="button"
            tabIndex={0}
            title="Личный кабинет"
            onClick={() => app.showView('profile')}
            onKeyDown={(e) => e.key === 'Enter' && app.showView('profile')}
          >
            <Avatar login={app.user.login} name={name} size={30} />
            <div className="acct-t">
              <b>{name}</b>
              <i>
                {/* должности важнее логина: по ним человек понимает свои права */}
                {positionsLabel(app.positions) || (app.user.isAdmin ? 'администратор' : app.user.login)}
              </i>
            </div>
          </div>
          <div className="acct-b">
            <a className="out" title="Выйти" onClick={app.logout}>
              <IconExit />
              <span>Выйти</span>
            </a>
          </div>
        </div>
        <button
          className="sb-collapse"
          title={min ? 'Развернуть меню' : 'Свернуть меню'}
          onClick={() => {
            setMin(!min);
            localStorage.setItem('code-sb-min', !min ? '1' : '0');
          }}
        >
          <IconCollapse />
        </button>
      </aside>
      <main
        className="main"
        onFocus={(e) => isTextField(e.target) && setTyping(true)}
        onBlur={(e) => isTextField(e.target) && setTyping(false)}
      >
        <div className="mtop">
          <Logo variant="full" className="mtop-logo" />
          <Bell variant="mobile" />
        </div>
        <UpdateBar />
        <div className="wrap">
          {/* Ошибка внутри раздела гасит только сам раздел: меню, шапка и
              вкладки остаются, и человек может уйти в другой. Ключ по разделу
              сбрасывает перехват при переходе — иначе упавший экран остался бы
              висеть и на следующем. */}
          <ErrorBoundary key={app.view}>
          <Suspense fallback={<div className="load-section">Загружаю раздел…</div>}>
            {ok && (
              <>
                {app.view === 'home' && <HomeScreen />}
                {app.view === 'kb' && <KbStack />}
                {app.view === 'projects' && <FunctionsSection />}
                {app.view === 'admin' && <Admin />}
                {app.view === 'calendar' && <CalendarScreen />}
                {app.view === 'chat' && <ChatScreen />}
                {app.view === 'contracts' && <ContractsSection />}
                {app.view === 'legal' && <LegalSection />}
                {app.view === 'finance' && <FinanceSection />}
            {app.view === 'tasks' && <MyAssignments />}
                {/* Личный кабинет — не стопка, но экран телефона такой же: обёртка
                    .nav-page делает его единственной прокручиваемой областью под
                    неподвижной шапкой (правила в styles.css). На десктопе обёртка
                    display:contents и в раскладке её нет. */}
                {app.view === 'profile' && (
                  <div className="nav-page">
                    <Profile />
                  </div>
                )}
                {app.view === 'more' && <MoreScreen />}
              </>
            )}
          </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <TabBar />
    </div>
  );
}
